import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  makeStyles,
  useTheme,
  IconButton,
} from "@material-ui/core";
import { ImgCotacao } from "@components/Feed/CotacaoInfo";
import { useSelector, useDispatch } from "react-redux";
import formatDate from "@utils/formatDate";
import { green, red } from "@material-ui/core/colors";
import { pvTurnToNumber } from "../../utils/showPreco";
import { Refresh } from "@material-ui/icons";
import cotacaoService from "../../services/cotacaoService";
import { setVencimentos } from "../../redux/cotacaoReducer";
import TCLoading from "../TCLoading";
import mixpanel from "@utils/mixpanel/mixpanel.js";
import { RELATORIO_FECHAMENTO_CONTRATOS } from "../../utils/mixpanel/mixpanelEvents";

const useStyles = makeStyles(() => ({
  watermarkWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 0,
    pointerEvents: "none",
    userSelect: "none",
  },
  watermarkText: {
    fontSize: "2rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
}));

function hexToRgb(hex) {
  const fullHex =
    hex.length === 4 ? "#" + [...hex.slice(1)].map((x) => x + x).join("") : hex;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
        result[3],
        16,
      )}`
    : "0, 0, 0";
}

const isToday = (date) => {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
};

const getFormattedDate = (date) =>
  new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const Watermark = ({ isSmall, down }) => {
  const classes = useStyles();
  const theme = useTheme();
  const color = `rgba(${hexToRgb(theme.palette.text.primary)}, 0.3)`;
  const smallStyles = isSmall ? { fontSize: "1.5rem" } : {};
  const alignStyles = down ? { top: "10%" } : {};
  return (
    <div className={classes.watermarkWrapper} style={alignStyles}>
      <Typography
        className={classes.watermarkText}
        style={{ color, ...smallStyles }}
      >
        cafetrade.com.br
      </Typography>
    </div>
  );
};

const renderPriceChange = (change) => {
  const num = Number(change);
  const isPositive = !isNaN(num) && num > 0;
  const isNegative = !isNaN(num) && num < 0;

  return (
    <span
      style={{
        color: isPositive ? green[500] : isNegative ? red[500] : "inherit",
      }}
    >
      {isNaN(num) ? change : `${num > 0 ? "+" : ""}${num.toFixed(2)}`}
    </span>
  );
};

const renderOIVariacao = (currentOI, yesterdayOI) => {
  const current = pvTurnToNumber(currentOI);
  const yesterday = pvTurnToNumber(yesterdayOI);

  if (isNaN(current) || isNaN(yesterday)) {
    return "-";
  }

  const variacao = current - yesterday;
  const isPositive = variacao > 0;
  const isNegative = variacao < 0;
  return (
    <span
      style={{
        color: isPositive ? green[500] : isNegative ? red[500] : "inherit",
      }}
    >
      {variacao === 0
        ? "0"
        : `${variacao > 0 ? "+" : ""}${variacao.toLocaleString()}`}
    </span>
  );
};

const noWrapStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  padding: "0px 1px",
};

const renderSpread = (list, index) => {
  if (index === 0) return ""; // first row has no previous price

  const current = pvTurnToNumber(list[index].lastPrice);
  const previous = pvTurnToNumber(list[index - 1].lastPrice);

  if (isNaN(current) || isNaN(previous)) return "-";

  const spread = current - previous;
  const isPositive = spread > 0;
  const isNegative = spread < 0;

  return (
    <span
      style={{
        color: isPositive ? green[500] : isNegative ? red[500] : "inherit",
      }}
    >
      {spread === 0 ? "0" : `${spread > 0 ? "+" : ""}${spread.toFixed(2)}`}
    </span>
  );
};
const renderRow = (v, index, list, isLondres = false) => {
  const data = v || {};
  const yesterdayOI = v?.yesterdayVencimento?.openInterest ?? "-";
  return (
    <TableRow key={v.contractSymbol}>
      <TableCell style={noWrapStyle}>
        <Box display="flex" alignItems="center" gridGap={4}>
          <ImgCotacao isLondres={isLondres} />
          {data.contractSymbol}
        </Box>
      </TableCell>
      <TableCell>{data.previousPrice ?? "-"}</TableCell>
      <TableCell style={{ fontWeight: "bold" }}>
        {data.lastPrice ?? "-"}
      </TableCell>
      <TableCell>{renderPriceChange(data.priceChange)}</TableCell>
      <TableCell>{renderSpread(list, index)}</TableCell>
      <TableCell>{data.openPrice ?? "-"}</TableCell>
      <TableCell>{data.highPrice ?? "-"}</TableCell>
      <TableCell>{data.lowPrice ?? "-"}</TableCell>
      <TableCell align="right">{data.volume ?? "-"}</TableCell>
      <TableCell align="right">{data.openInterest ?? "-"}</TableCell>
      <TableCell align="right">{yesterdayOI}</TableCell>
      <TableCell align="right">
        {renderOIVariacao(data.openInterest, yesterdayOI)}
      </TableCell>
      <TableCell style={noWrapStyle}>
        {data.tradeTime
          ? typeof data.tradeTime === "number"
            ? new Date(data.tradeTime * 1000).toLocaleString()
            : formatDate(data.updatedAt, false, true)
          : "-"}
      </TableCell>
    </TableRow>
  );
};
export default function Contrato() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [cotacao] = useSelector((states) => [states.cotacaoReducer]);
  const fetchCotacao = useCallback(() => {
    setLoading(true);
    cotacaoService
      .getVencimentos()
      .then((rVencimentos) => dispatch(setVencimentos(rVencimentos)))
      .finally(() => {
        setTimeout(() => setLoading(false), 2000);
      });
  }, [dispatch]);

  useEffect(() => {
    mixpanel.track(RELATORIO_FECHAMENTO_CONTRATOS);
    const interval = setInterval(
      () => {
        fetchCotacao();
      },
      5 * 60 * 1000,
    ); // 5 minutos
    return () => clearInterval(interval);
  }, [fetchCotacao]);

  const arabicaList = [];
  const robustaList = [];
  Object.values(cotacao?.vencimentos || {})
    .filter((v) => !v.expired)
    .forEach((v) => {
      if (v.preSymbol === "RM") robustaList.push(v);
      else arabicaList.push(v);
    });

  const today = new Date();
  const dayOfWeek = today.getDay();

  let isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  const processList = (list) => {
    const allUpdatedToday = list.every((v) => isToday(v.updatedAt));
    const latestUpdatedAt = list.reduce((latest, v) => {
      const updated = v.updatedAt ? new Date(v.updatedAt) : new Date(0);
      return updated > latest ? updated : latest;
    }, new Date(0));
    const referenceDate = getFormattedDate(latestUpdatedAt);
    return { list, referenceDate, allUpdatedToday };
  };

  const sumCol = (list, accessor) =>
    list.reduce((acc, item) => acc + (pvTurnToNumber(accessor(item)) || 0), 0);

  const renderTotalRow = (list) => {
    const totalVolume = sumCol(list, (v) => v.volume);
    const totalOI = sumCol(list, (v) => v.openInterest);
    const totalOIY = sumCol(list, (v) => v.yesterdayVencimento?.openInterest);
    const totalVar = totalOI - totalOIY;

    return (
      <TableRow>
        <TableCell colSpan={8} align="right">
          <strong>Total:</strong>
        </TableCell>
        <TableCell align="right">{totalVolume.toLocaleString()}</TableCell>
        <TableCell align="right">{totalOI.toLocaleString()}</TableCell>
        <TableCell align="right">{totalOIY.toLocaleString()}</TableCell>
        <TableCell align="right">
          {renderOIVariacao(totalOI, totalOIY)}
        </TableCell>
        <TableCell />
      </TableRow>
    );
  };

  const renderTable = (title, data, isLondres) => (
    <TableContainer
      component={Paper}
      style={{ position: "relative", marginTop: 20 }}
    >
      <Watermark />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <Typography variant="h6">
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <ImgCotacao height={18} isLondres={isLondres} />
            {title} – {data.referenceDate}
          </div>
        </Typography>
        <IconButton
          size="small"
          onClick={fetchCotacao}
          style={{ cursor: "pointer" }}
        >
          <Refresh fontSize="small" />
        </IconButton>
      </Box>
      {isWeekend
        ? null
        : !data.allUpdatedToday && (
            <Box px={2}>
              <Typography color="textSecondary">
                🕒 Aguardando fechamento de hoje: {getFormattedDate(new Date())}
              </Typography>
            </Box>
          )}
      <Table
        size="small"
        style={{ position: "relative", zIndex: 1, marginTop: 20 }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Contrato</TableCell>
            <TableCell style={noWrapStyle}>Anterior</TableCell>
            <TableCell style={noWrapStyle}>Últ Preço</TableCell>
            <TableCell style={noWrapStyle}>Variação</TableCell>
            <TableCell style={noWrapStyle}>Spread</TableCell>
            <TableCell style={noWrapStyle}>Abertura</TableCell>
            <TableCell style={noWrapStyle}>Máximo</TableCell>
            <TableCell style={noWrapStyle}>Mínimo</TableCell>
            <TableCell style={noWrapStyle} align="right">
              Volume
            </TableCell>{" "}
            <TableCell style={noWrapStyle} align="right">
              OI
            </TableCell>
            <TableCell style={noWrapStyle} align="right">
              OI Anterior
            </TableCell>{" "}
            <TableCell style={noWrapStyle} align="right">
              Variação
            </TableCell>
            <TableCell>Data</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.list.map((v, idx) => renderRow(v, idx, data.list, isLondres))}
          {renderTotalRow(data.list)}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const arabica = processList(arabicaList);
  const robusta = processList(robustaList);

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <TCLoading size={40} />
        </Box>
      ) : (
        <>
          {renderTable("Arábica", arabica, false)}
          {renderTable("Robusta", robusta, true)}
        </>
      )}
    </Box>
  );
}
